/* eslint-disable react/jsx-key */
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { StatsTableBody } from 'widgets/statspro/js/components/common/stats-table/StatsTableBody';
import { useGetInterchangeQuery } from 'common/store/modules/api/cfs-api';

import {
    createJumperNumberCellRenderer,
    createPlayerCellRenderer,
    renderEmptyCell
} from './TableCellRenderers';

const COMPS_WITH_PROFILE_LINKS = new Set([
    PULSE.app.common.CONSTANTS.COMPETITION.PREMIERSHIP.code,
    PULSE.app.common.CONSTANTS.COMPETITION.WOMENS.code,
    PULSE.app.common.CONSTANTS.COMPETITION.ORIGIN.code
]);

/**
 * @param {object} props - Props.
 * @param {object[]} props.rows - Rows to render.
 * @param {Function} props.getTableBodyProps - Get table body props function.
 * @param {Function} props.prepareRow - Prepare row function.
 * @param {string} props.compPid - Competition PID.
 * @param {string} props.matchId - Match ID.
 * @returns {JSX.Element} Component.
 */
export const TableBody = ({
    rows,
    getTableBodyProps,
    prepareRow,
    compPid,
    matchId
}) => {
    const compAbbr = PULSE.app.common.match.getCompAbbr(compPid);
    const isAFLW = compAbbr === 'AFLW';
    const isMainCompetition = COMPS_WITH_PROFILE_LINKS.has(compAbbr);

    const { data: interchangeData } = useGetInterchangeQuery(matchId);

    // Build memoised lookup map (playerId -> benchReason)
    const benchReasonMap = useMemo(() => {
        if (!interchangeData) {
            return {};
        }

        const map = {};
        const allInterchanges = [
            ...(interchangeData.homeInterchange || []),
            ...(interchangeData.awayInterchange || [])
        ];

        allInterchanges.forEach((item) => {
            if (item?.player?.playerId) {
                map[item.player.playerId] = item.benchReason;
            }
        });

        return map;
    }, [interchangeData]);

    const customCells = useMemo(
        () => ({
            jumperNumber: createJumperNumberCellRenderer(
                isMainCompetition,
                isAFLW
            ),
            player: createPlayerCellRenderer(
                isMainCompetition,
                isAFLW,
                benchReasonMap
            ),
            empty: renderEmptyCell
        }),
        [isMainCompetition, isAFLW, benchReasonMap]
    );

    return (
        <StatsTableBody
            rows={rows}
            getTableBodyProps={getTableBodyProps}
            prepareRow={prepareRow}
            customCells={customCells}
        />
    );
};

TableBody.propTypes = {
    rows: PropTypes.array.isRequired,
    getTableBodyProps: PropTypes.func.isRequired,
    prepareRow: PropTypes.func.isRequired,
    compPid: PropTypes.string,
    matchId: PropTypes.string.isRequired
};
